import { Employee, employeeDisplayName, employeeInitials } from './employee.model';
import { User } from './user.model';

/**
 * Who a vacation is for. `employee` is an org chart person (most have no Prism login), `user` is a
 * fishbowl profile, and `custom` is a free-typed name for anyone in neither.
 */
export type VacationEmployeeType = 'employee' | 'user' | 'custom';

export interface VacationEvent {
    id: string;
    employeeType: VacationEmployeeType;
    /** Org chart employee when `employeeType` is `employee`; null otherwise. */
    employeeId: string | null;
    employeeUserId: string | null;
    customEmployeeName: string;
    customEmployeeTitle: string;
    startDate: Date;
    endDate: Date;
    notes: string;
    createdById: string;
    createdOn: Date;
    updatedById: string;
    updatedOn: Date;
}

export const DEFAULT_VACATION_EVENT: VacationEvent = {
    id: '',
    employeeType: 'employee',
    employeeId: null,
    employeeUserId: null,
    customEmployeeName: '',
    customEmployeeTitle: '',
    startDate: new Date(),
    endDate: new Date(),
    notes: '',
    createdById: '',
    createdOn: new Date(),
    updatedById: '',
    updatedOn: new Date(),
};

export interface VacationEmployeeDisplay {
    name: string;
    title: string;
    imageUrl: string;
    /** Shown in place of a portrait when there is no image. */
    initials: string;
}

/** Name, title and portrait for whoever a vacation is for, whichever kind of person that is. */
export function vacationEmployeeDisplay(
    event: VacationEvent,
    userDict: Record<string, User> = {},
    employeeDict: Record<string, Employee> = {},
): VacationEmployeeDisplay {
    if (event.employeeType === 'employee') {
        const employee = event.employeeId ? employeeDict[event.employeeId] : null;
        if (!employee) {
            return { name: 'unknown employee', title: '', imageUrl: '', initials: '?' };
        }
        return {
            name: employeeDisplayName(employee),
            title: employee.jobTitle ?? '',
            imageUrl: employee.thumbnailImageUrl || employee.imageUrl || '',
            initials: employeeInitials(employee),
        };
    }

    if (event.employeeType === 'custom') {
        const name = event.customEmployeeName || 'custom employee';
        return { name, title: event.customEmployeeTitle, imageUrl: '', initials: initialsOf(name) };
    }

    const user = event.employeeUserId ? userDict[event.employeeUserId] : null;
    if (!user) {
        return { name: 'unknown employee', title: '', imageUrl: '', initials: '?' };
    }
    const name = `${user.firstName} ${user.lastName}`;
    return {
        name,
        title: user.email ?? '',
        imageUrl: user.thumbnailImageUrl || user.imageUrl || '',
        initials: initialsOf(name),
    };
}

function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return `${parts[0]?.charAt(0) ?? ''}${parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''}`.toUpperCase();
}
